import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { createContext, useContext, useEffect, useState } from 'react';
import { keyframes, css } from 'styled-components';
import * as animations from 'react-animations';
import { Transition } from 'react-transition-group';
import { FocusOn } from 'react-focus-on';
import { noop } from 'lodash';
import Portal from '../Portal';
import { Flex } from '../Flex';
import { Icon } from '../Icon';
import { createComponent, themeGet } from '../utils';
var ModalContext = createContext({});
var getAnimation = function (name) { return keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["", ""], ["", ""])), animations[name]); };
var ModalContainer = createComponent({
    name: 'ModalContainer',
    style: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    z-index: 1000;\n    padding: 1rem;\n    display: flex;\n    position: fixed;\n    align-items: center;\n    overflow-y: auto;\n    overflow-x: hidden;\n    justify-content: center;\n  "], ["\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    z-index: 1000;\n    padding: 1rem;\n    display: flex;\n    position: fixed;\n    align-items: center;\n    overflow-y: auto;\n    overflow-x: hidden;\n    justify-content: center;\n  "]))),
});
var Backdrop = createComponent({
    name: 'ModalBackdrop',
    style: function (_a) {
        var transitionState = _a.transitionState;
        return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    position: fixed;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    background: rgba(0, 0, 0, 0.4);\n    ", "\n\n    ", ";\n\n    ", ";\n  "], ["\n    position: fixed;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    background: rgba(0, 0, 0, 0.4);\n    ",
            "\n\n    ",
            ";\n\n    ",
            ";\n  "])), transitionState === 'exited' && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      display: none;\n    "], ["\n      display: none;\n    "]))), transitionState === 'entering' && css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n      animation: 0.35s ", ";\n    "], ["\n      animation: 0.35s ", ";\n    "])), getAnimation('fadeIn')), transitionState === 'exiting' && css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n      animation: 0.35s ", ";\n    "], ["\n      animation: 0.35s ", ";\n    "])), getAnimation('fadeOut')));
    },
});
var ModalContent = createComponent({
    name: 'ModalContent',
    style: function (_a) {
        var minWidth = _a.minWidth, maxWidth = _a.maxWidth, transitionState = _a.transitionState, animationIn = _a.animationIn, animationOut = _a.animationOut, theme = _a.theme;
        return css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    position: relative;\n    margin: auto;\n    min-width: ", "px;\n    max-width: ", "px;\n    background: #ffffff;\n    background-clip: padding-box;\n    box-shadow: ", ";\n    border-radius: ", "px;\n    max-height: 90vh;\n    outline: none;\n\n    ", ";\n\n    ", ";\n\n    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {\n      position: fixed;\n      top: 50%;\n      left: 50%;\n      transform: translate(-50%, -50%);\n    }\n  "], ["\n    position: relative;\n    margin: auto;\n    min-width: ", "px;\n    max-width: ", "px;\n    background: #ffffff;\n    background-clip: padding-box;\n    box-shadow: ", ";\n    border-radius: ", "px;\n    max-height: 90vh;\n    outline: none;\n\n    ",
            ";\n\n    ",
            ";\n\n    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {\n      position: fixed;\n      top: 50%;\n      left: 50%;\n      transform: translate(-50%, -50%);\n    }\n  "])), minWidth, maxWidth, theme.shadow.hard, themeGet('radius'), transitionState === 'entering' && css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n      animation: 0.75s ", ";\n    "], ["\n      animation: 0.75s ", ";\n    "])), getAnimation(animationIn)), transitionState === 'exiting' && css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n      animation: 0.75s ", ";\n    "], ["\n      animation: 0.75s ", ";\n    "])), getAnimation(animationOut)));
    },
});
export var Modal = function (_a) {
    var children = _a.children, title = _a.title, animationDuration = _a.animationDuration, showClose = _a.showClose, onClose = _a.onClose, open = _a.open, props = __rest(_a, ["children", "title", "animationDuration", "showClose", "onClose", "open"]);
    var _b = __read(useState(open), 2), isOpen = _b[0], setOpen = _b[1];
    var modalRef = React.useRef(null);
    var handleClose = function () {
        setOpen(false);
        onClose();
    };
    var handleContentClick = function (event) { return event.stopPropagation(); };
    var handleBackdropClick = function () {
        if (!props.closeOnBackdropClick) {
            return;
        }
        handleClose();
    };
    var scrollToTop = function () {
        if (modalRef.current && modalRef.current.scroll) {
            modalRef.current.scroll(0, 0);
        }
    };
    useEffect(function () {
        if (open !== isOpen) {
            setOpen(open);
        }
    }, [open, isOpen]);
    return (React.createElement(ModalContext.Provider, { value: { handleClose: handleClose } },
        React.createElement(Portal, null,
            React.createElement(Transition, { in: isOpen, timeout: animationDuration, onEntering: scrollToTop, mountOnEnter: true, unmountOnExit: true, appear: true, addEndListener: noop }, function (state) { return (React.createElement(FocusOn, { onEscapeKey: handleClose, enabled: isOpen },
                React.createElement(ModalContainer, { ref: modalRef },
                    React.createElement(Backdrop, { transitionState: state, onClick: handleBackdropClick }),
                    React.createElement(ModalContent, __assign({ transitionState: state, onClick: handleContentClick, "aria-modal": "true", tabIndex: 0 }, props),
                        title && React.createElement(Modal.Header, { title: title, showClose: showClose }),
                        children)))); }))));
};
Modal.defaultProps = {
    open: false,
    showClose: true,
    closeOnBackdropClick: true,
    closeOnEscape: true,
    minWidth: 350,
    maxWidth: 350,
    animationIn: 'zoomIn',
    animationOut: 'zoomOut',
    animationDuration: 175,
    onClose: noop,
};
Modal.Title = createComponent({
    name: 'ModalTitle',
    tag: 'h2',
    style: css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n    font-size: 16px;\n    margin: 0;\n    outline: none;\n  "], ["\n    font-size: 16px;\n    margin: 0;\n    outline: none;\n  "]))),
});
var ModalHeader = createComponent({
    name: 'ModalHeader',
    style: css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n    padding: 1rem 1.25rem 0;\n    border-top-left-radius: ", "px;\n    border-top-right-radius: ", "px;\n  "], ["\n    padding: 1rem 1.25rem 0;\n    border-top-left-radius: ", "px;\n    border-top-right-radius: ", "px;\n  "])), themeGet('radius'), themeGet('radius')),
});
var ModalHeaderInner = createComponent({
    name: 'ModalHeaderInner',
    style: function (_a) {
        var theme = _a.theme;
        return css(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n    border-bottom: 1px solid ", ";\n  "], ["\n    border-bottom: 1px solid ", ";\n  "])), theme.colors.grey);
    },
});
Modal.Header = function (_a) {
    var title = _a.title, children = _a.children, _b = _a.showClose, showClose = _b === void 0 ? true : _b;
    var handleClose = useContext(ModalContext).handleClose;
    var handleKeyDown = function (_a) {
        var keyCode = _a.keyCode;
        if (keyCode === 13) {
            handleClose();
        }
    };
    return (React.createElement(ModalHeader, null,
        React.createElement(ModalHeaderInner, null,
            React.createElement(Flex, { alignItems: "center" },
                title && React.createElement(Modal.Title, { role: "heading" }, title),
                children,
                showClose && (React.createElement(Icon, { style: { marginLeft: 'auto', marginRight: -6, outline: 'none' }, onClick: handleClose, onKeyDown: handleKeyDown, "aria-label": "Close Modal", role: "button", tabIndex: -1, name: "close", color: "greyDarkest", size: 24 }))))));
};
Modal.Body = createComponent({
    name: 'ModalBody',
    style: css(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n    padding: 1.25rem;\n    max-height: calc(90vh - 105px);\n    overflow-y: auto;\n  "], ["\n    padding: 1.25rem;\n    max-height: calc(90vh - 105px);\n    overflow-y: auto;\n  "]))),
});
Modal.Footer = createComponent({
    name: 'ModalFooter',
    style: css(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n    padding: 0 1.25rem 1rem;\n    border-bottom-left-radius: ", "px;\n    border-bottom-right-radius: ", "px;\n  "], ["\n    padding: 0 1.25rem 1rem;\n    border-bottom-left-radius: ", "px;\n    border-bottom-right-radius: ", "px;\n  "])), themeGet('radius'), themeGet('radius')),
});
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14;
//# sourceMappingURL=Modal.js.map